import {
  evidenceFieldLabel,
  ISmartLink,
  ISmartLinkEvidence,
  SmartLinkEvidenceField,
} from './i-smart-link';
import { ISmartLinkRunContent } from './i-smart-link-run-request';

// Three parts rather than one marked-up string, so the card can highlight the match without
// rendering HTML built from article content.
export interface ISmartLinkSnippet {
  before: string;
  match: string;
  after: string;
}

// Roughly two lines of text-xs at panel width. The card still clamps, so this is only a budget.
export const SNIPPET_MAX_CHARS = 96;

// Capped so the match itself is never pushed off the first line.
const SNIPPET_LEAD_IN_CHARS = 24;

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: '\'',
  nbsp: ' ',
};

// Anything unrecognised becomes a space rather than leaking "&frac12;" onto the card.
const decodeEntity = (entity: string): string => {
  const isHex = /^#x/i.test(entity);
  if (isHex || entity.startsWith('#')) {
    const code = parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10);
    return Number.isNaN(code) ? ' ' : String.fromCodePoint(code);
  }
  return NAMED_ENTITIES[entity.toLowerCase()] ?? ' ';
};

// Separate from hasScannableText()'s stripper in related-data.component.ts: that one only decides
// whether any non-space survives, so it can flatten every entity to a space. This one is read by a
// human, so "&amp;" has to come back as an ampersand. Tags become spaces, not nothing.
export function toPlainText(value: string): string {
  return (value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(
      /&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi,
      (_whole, entity: string) => decodeEntity(entity),
    );
}

const normalise = (value: string): string =>
  (value || '').replace(/\s+/g, ' ').trim();

// Never open the snippet on half a word.
function leadingContext(source: string, at: number, maxChars: number): string {
  if (at <= 0 || maxChars <= 0) {
    return '';
  }
  let start = Math.max(0, at - maxChars);
  if (start > 0) {
    const boundary = source.indexOf(' ', start);
    start = boundary === -1 || boundary >= at ? at : boundary + 1;
  }
  const text = source.slice(start, at);
  return start > 0 ? `…${text}` : text;
}

function trailingContext(
  source: string,
  from: number,
  maxChars: number,
): string {
  if (from >= source.length || maxChars <= 0) {
    return '';
  }
  let end = Math.min(source.length, from + maxChars);
  if (end < source.length) {
    const boundary = source.lastIndexOf(' ', end);
    end = boundary <= from ? end : boundary;
  }
  const text = source.slice(from, end);
  return end < source.length ? `${text}…` : text;
}

// Both sides are whitespace-normalised before matching, and the normalised source is what gets
// sliced: the body is HTML, so the run's matched_text is single-spaced where the article has tags
// and newlines. When the lookup fails — a further-normalised phrase, or a body edited after the
// run — the snippet degrades to the matched text alone. It must never come back empty.
export function buildEvidenceSnippet(
  evidence: ISmartLinkEvidence,
  content: ISmartLinkRunContent | null,
): ISmartLinkSnippet {
  const withoutContext: ISmartLinkSnippet = {
    before: '',
    match: evidence.matched_text,
    after: '',
  };

  const needle = normalise(evidence.matched_text);
  if (!needle || !content) {
    return withoutContext;
  }

  const source = normalise(toPlainText(content[evidence.field]));
  const at = source.toLowerCase().indexOf(needle.toLowerCase());
  if (at === -1) {
    return withoutContext;
  }

  // Sliced from the source, not the evidence, so the card shows the article's own casing.
  const match = source.slice(at, at + needle.length);
  const budget = SNIPPET_MAX_CHARS - match.length;
  if (budget <= 0) {
    return { before: '', match, after: '' };
  }

  const before = leadingContext(
    source,
    at,
    Math.min(SNIPPET_LEAD_IN_CHARS, budget),
  );
  const after = trailingContext(
    source,
    at + match.length,
    budget - before.length,
  );
  return { before, match, after };
}

// One rendered evidence line: several citations of the same record in the same field collapse
// into a single row with a count.
export interface ISmartLinkEvidenceRow {
  field: SmartLinkEvidenceField;
  label: string;
  count: number;
  countLabel: string;
  snippet: ISmartLinkSnippet;
  title: string;
  // Every clause the row stands for, not just the one it quotes.
  clauses: string[];
}

// Shared by the card and the mention-block header, so the two cannot quote a mention differently.
export function buildEvidenceRows(
  item: ISmartLink,
  content: ISmartLinkRunContent | null,
): ISmartLinkEvidenceRow[] {
  const byField = new Map<SmartLinkEvidenceField, ISmartLinkEvidence[]>();
  for (const evidence of item?.evidence ?? []) {
    const citations = byField.get(evidence.field);
    if (citations) {
      citations.push(evidence);
    } else {
      byField.set(evidence.field, [evidence]);
    }
  }

  return Array.from(byField.values()).map((citations) => ({
    field: citations[0].field,
    label: evidenceFieldLabel(citations[0].field),
    count: citations.length,
    countLabel: `${citations.length} citation${citations.length === 1 ? '' : 's'}`,
    snippet: buildEvidenceSnippet(citations[0], content),
    // Every citation, so the ones the count stands for are still readable on hover.
    title: citations.map((citation) => citation.matched_text).join(' · '),
    clauses: citations.map((citation) => citation.matched_text),
  }));
}
